import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/authStore';

// Landing
import LandingPage from './pages/LandingPage';

// Auth pages
import LoginPage          from './pages/LoginPage';
import RegisterPage       from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage  from './pages/ResetPasswordPage';
import OAuthCallbackPage  from './pages/OAuthCallbackPage';

// App pages
import DashboardPage    from './pages/DashboardPage';
import FoldersPage      from './pages/FoldersPage';
import CategoriesPage   from './pages/CategoriesPage';
import UpgradePage      from './pages/UpgradePage';
import SettingsPage     from './pages/SettingsPage';

// Admin pages
import AdminPage         from './pages/AdminPage';
import AdminSettingsPage from './pages/AdminSettingsPage';

// Public pages
import SharedImagePage from './pages/SharedImagePage';
import NotFoundPage    from './pages/NotFoundPage';

// Layout
import AppLayout from './components/layout/AppLayout';

// PWA install prompt
import InstallPrompt from './components/ui/InstallPrompt';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  return user ? <Navigate to="/dashboard" replace /> : children;
}

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <InstallPrompt />
      <Routes>
        {/* Landing */}
        <Route path="/" element={<LandingPage />} />

        {/* Public — no auth needed */}
        <Route path="/share/:token" element={<SharedImagePage />} />

        {/* Guest-only */}
        <Route path="/login"           element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register"        element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
        <Route path="/reset-password"  element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />
        <Route path="/oauth-callback"  element={<OAuthCallbackPage />} />

        {/* Authenticated */}
        <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
          <Route path="/dashboard"   element={<DashboardPage />} />
          <Route path="/folders"     element={<FoldersPage />} />
          <Route path="/categories"  element={<CategoriesPage />} />
          <Route path="/upgrade"     element={<UpgradePage />} />
          <Route path="/settings"    element={<SettingsPage />} />

          {/* Admin */}
          <Route path="/admin"          element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AuthProvider>
  );
}
